import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, FileText, Calendar, Clock, CheckCircle2, AlertCircle, Search, Download, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { studentService } from '../../services/studentService';

const Assignments = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'completed'
    const [loading, setLoading] = useState(true);
    const [student, setStudent] = useState(null);

    // Dummy Data
    const dummyAssignments = {
        pending: [
            {
                id: 1,
                title: "Trigonometry Basics - Chapter 4",
                subject: "Mathematics",
                teacher: "Mr. Sharma",
                assignedDate: "2026-02-01",
                deadline: "2026-02-05",
                status: "Pending",
                description: "Solve all exercises from page 45 to 50. Show all steps clearly.",
                priority: "High"
            },
            {
                id: 2,
                title: "Atomic Structure Lab Report",
                subject: "Chemistry",
                teacher: "Dr. Bose",
                assignedDate: "2026-02-02",
                deadline: "2026-02-07",
                status: "Pending",
                description: "Submit the lab report for the experiments done last Friday.",
                priority: "Medium"
            },
            {
                id: 3,
                title: "Renaissance Art History Essay",
                subject: "History",
                teacher: "Mrs. Das",
                assignedDate: "2026-01-30",
                deadline: "2026-02-10",
                status: "In Progress",
                priority: "Low"
            }
        ],
        completed: [
            {
                id: 101,
                title: "Grammar & Composition Unit 1",
                subject: "English",
                teacher: "Ms. Roy",
                assignedDate: "2026-01-15",
                completedDate: "2026-01-20",
                grade: "A+",
                status: "Graded"
            },
            {
                id: 102,
                title: "Python Lists & Dictionaries",
                subject: "Computer Science",
                teacher: "Mr. Gupta",
                assignedDate: "2026-01-10",
                completedDate: "2026-01-14",
                grade: "A",
                status: "Graded"
            }
        ]
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchStudentData();
    }, []);

    const fetchStudentData = async () => {
        try {
            setLoading(true);
            const studentId = localStorage.getItem("selectedStudentId");
            if (!studentId) {
                toast.error("No student selected");
                navigate('/dashboard');
                return;
            }
            const response = await studentService.getStudentEnrollment(studentId);
            const enrollmentData = Array.isArray(response.data) ? response.data[0] : response.data;
            if (enrollmentData) {
                setStudent({
                    name: enrollmentData.student?.name || enrollmentData.student?.firstName || "N/A",
                    class: enrollmentData.class?.name || "N/A",
                    section: enrollmentData.section || "A",
                    imageUrl: enrollmentData.student?.profilePicture || "/un.png",
                });
            }
        } catch (error) {
            console.error("Error fetching student profile:", error);
        } finally {
            setLoading(false);
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return 'bg-red-100 text-red-600';
            case 'Medium': return 'bg-orange-100 text-orange-600';
            default: return 'bg-blue-100 text-blue-600';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-pragyaBlue/20 border-t-pragyaBlue rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-32">
            {/* Header Section */}
            <div className="bg-white px-6 pt-6 pb-2 rounded-b-[2.5rem] shadow-sm border-b border-slate-100 sticky top-0 z-30">
                <div className="flex items-center justify-between mb-6">
                    <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-slate-50">
                        <ChevronLeft size={24} className="text-slate-600" />
                    </button>
                    <h2 className="text-lg font-bold text-slate-800">Assignments</h2>
                    <div className="w-10" />
                </div>

                {/* Tabs */}
                <div className="flex p-1 bg-slate-100 rounded-2xl mb-4">
                    <button
                        onClick={() => setActiveTab('pending')}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'pending' ? 'bg-white text-pragyaBlue shadow-sm' : 'text-slate-500'}`}
                    >
                        My Assignments
                        {dummyAssignments.pending.length > 0 && (
                            <span className="ml-2 bg-pragyaBlue/10 text-pragyaBlue px-2 py-0.5 rounded-full text-[10px]">
                                {dummyAssignments.pending.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('completed')}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'completed' ? 'bg-white text-pragyaBlue shadow-sm' : 'text-slate-500'}`}
                    >
                        Completed
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="px-5 mt-6">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                    >
                        {activeTab === 'pending' ? (
                            dummyAssignments.pending.map((item, idx) => (
                                <div key={item.id} className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${getPriorityColor(item.priority)}`}>
                                            {item.priority} Priority
                                        </div>
                                        <div className="text-xs font-bold text-slate-400 flex items-center gap-1">
                                            <Clock size={12} />
                                            {item.deadline}
                                        </div>
                                    </div>
                                    <h3 className="text-md font-extrabold text-slate-800 mb-1 leading-tight">{item.title}</h3>
                                    <p className="text-slate-400 text-xs font-semibold mb-4">{item.subject} • By {item.teacher}</p>

                                    <div className="bg-slate-50 rounded-2xl p-4 mb-5 border border-slate-100">
                                        <p className="text-slate-600 text-xs leading-relaxed">{item.description || "No detailed description provided for this assignment."}</p>
                                    </div>

                                    <div className="flex gap-3">
                                        <button className="flex-1 bg-pragyaBlue text-white py-3 rounded-xl text-xs font-bold hover:bg-pragyaBlue/90 transition-colors">
                                            Submit Work
                                        </button>
                                        <button className="p-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors">
                                            <Download size={18} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            dummyAssignments.completed.map((item, idx) => (
                                <div key={item.id} className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 flex items-center gap-4">
                                    <div className="w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center text-green-500">
                                        <CheckCircle2 size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-sm font-bold text-slate-800 mb-0.5">{item.title}</h3>
                                        <p className="text-slate-400 text-[11px] font-medium">{item.subject} • Graded: <span className="text-green-600 font-bold">{item.grade}</span></p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-black text-slate-300 uppercase leading-none mb-1">Date</p>
                                        <p className="text-[11px] font-bold text-slate-500">{item.completedDate}</p>
                                    </div>
                                </div>
                            ))
                        )}

                        {dummyAssignments[activeTab].length === 0 && (
                            <div className="text-center py-20">
                                <AlertCircle size={48} className="mx-auto text-slate-200 mb-4" />
                                <h3 className="text-slate-400 font-bold">No assignments found</h3>
                                <p className="text-slate-300 text-xs">You're all caught up!</p>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Assignments;
